import { useMemo } from "react";

import { PageOcrLines, useOcrLinesForPages } from "@hooks/queries/use-ocr-lines";

import { OcrHighlightRequest } from "../types";
import { matchSnippetToLines } from "../utils/matchSnippetToLines";

export type HighlightRequest = OcrHighlightRequest;

export type HighlightRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type OcrHighlights = {
  /** Matched highlight rectangles (normalized 0-1) grouped by 1-based page. */
  rectsByPage: Map<number, HighlightRect[]>;
  /** The 1-based page of the first (primary) highlight, for scroll targeting. */
  primaryPage: number | null;
};

/**
 * Resolve cited passages to highlight rectangles over a scanned PDF (MDRS-134).
 *
 * Preferred path: the backend returns exact per-line bounding boxes for the
 * cited chunk (`bboxes`), which are drawn directly — no OCR fetch, no matching.
 * A chunk can span pages; each box is drawn on its own `page` when it has one.
 *
 * Fallback path (older backend responses with no `bboxes`): fetch the page's
 * stored OCR lines and match the snippet text against them. Pages whose OCR
 * isn't ready, or whose snippet doesn't match, contribute no rectangles and the
 * caller falls back to a plain page jump.
 */
export const useOcrHighlights = (
  caseNumber: string | undefined,
  fileName: string | undefined,
  highlights: HighlightRequest[] | undefined
): OcrHighlights => {
  // Only the pages that still need snippet matching (no backend bboxes) drive
  // an OCR fetch; pages that already carry exact boxes don't hit the network.
  const pagesNeedingOcr = (highlights ?? [])
    .filter((h) => !h.bboxes || h.bboxes.length === 0)
    .map((h) => h.page);
  const pageOcr = useOcrLinesForPages(caseNumber, fileName, pagesNeedingOcr);

  return useMemo(() => {
    const rectsByPage = new Map<number, HighlightRect[]>();
    if (!highlights || highlights.length === 0) {
      return { rectsByPage, primaryPage: null };
    }

    const ocrByPage = new Map<number, PageOcrLines>();
    for (const entry of pageOcr) ocrByPage.set(entry.page, entry);

    const addRects = (page: number, rects: HighlightRect[]) => {
      if (rects.length === 0) return;
      const existing = rectsByPage.get(page) ?? [];
      rectsByPage.set(page, [...existing, ...rects]);
    };

    for (const { page, snippet, bboxes } of highlights) {
      // Preferred: exact boxes straight from the backend. Each box carries its
      // own page when the chunk spans pages; older responses omit it and every
      // box belongs to the passage's page.
      if (bboxes && bboxes.length > 0) {
        for (const b of bboxes) {
          addRects(b.page ?? page, [
            { x: b.x, y: b.y, width: b.width, height: b.height },
          ]);
        }
        continue;
      }

      // Fallback: match the snippet against fetched OCR lines.
      const ocr = ocrByPage.get(page);
      if (!ocr || ocr.lines.length === 0) continue;

      const matched = matchSnippetToLines(
        ocr.lines.map((l) => l.text),
        snippet
      );
      if (!matched) continue;

      addRects(
        page,
        matched.map((i) => {
          const line = ocr.lines[i];
          return {
            x: line.x,
            y: line.y,
            width: line.width,
            height: line.height,
          };
        })
      );
    }

    return { rectsByPage, primaryPage: highlights[0]?.page ?? null };
  }, [highlights, pageOcr]);
};
